import React, { useState, useEffect, useMemo, useCallback } from 'react';
// import {
//   Button,
// } from '@material-ui/core';
import { ReactFormGenerator, ElementStore } from 'components_newui/formBuilder/react-form-builder-innovate/src/index';
import { parseArraySafely } from '../utils';
import CustomButton from 'components_newui/customButton/CustomButton';


/*****************************
 * Scorecard - Single File JS
 *****************************/

/** Safe numeric coercion */
const coerceNum = (v) => {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const trimmed = v.trim();
    if (trimmed === '') return NaN;
    const num = Number(trimmed);
    return Number.isFinite(num) ? num : NaN;
  }
  if (typeof v === 'boolean') return v ? 1 : 0;
  return NaN;
};

/**
 * Build lookup: element.id -> option.key -> option.value (fallback to text)
 * Supports RadioButtons/Checkboxes/Dropdown (same options shape).
 */
const buildOptionKeyValueMap = (formDef) =>
  (Array.isArray(formDef) ? formDef : []).reduce((acc, el) => {
    const isChoice =
      el &&
      (el.element === 'RadioButtons' ||
        el.element === 'Checkboxes' ||
        el.element === 'Dropdown');

    if (!isChoice || !Array.isArray(el.options) || !el.id) return acc;

    const keyMap = el.options.reduce((m, opt) => {
      if (!opt || !opt.key) return m;
      // prefer value; fallback to text
      const logical = (opt.value ?? opt.text ?? '').toString();
      m[opt.key] = logical;
      return m;
    }, {});

    acc[el.id] = keyMap;
    return acc;
  }, {});

/**
 * Resolve raw answer:
 * - Picks value/selectedValue/answer
 * - If array (e.g., ["radiobuttons_option_*"]), use first
 * - If key is found in optionKeyValueMap[id], translate to logical (e.g., "yes"/"may_be"/"no")
 */
const resolveRawAnswer = (resp, optionKeyValueMap) => {
  const id = resp && resp.id;

  const candidate =
    (resp && (resp.value ?? resp.selectedValue ?? resp.answer)) ?? resp;

  const first =
    Array.isArray(candidate) && candidate.length > 0 ? candidate[0] : candidate;

  const keyMap = optionKeyValueMap && id && optionKeyValueMap[id];
  if (keyMap &&
    typeof first === 'string' &&
    Object.prototype.hasOwnProperty.call(keyMap, first)) {
    return keyMap[first];
  }

  return first;
};

/**
 * Map radio-like strings to 0..10 scale:
 * - yes    -> 10
 * - may_be -> 5 (also accepts "maybe"/"may be")
 * - no     -> 0
 * Otherwise tries numeric coercion (for normal ratings like 8/10).
 */
const mapRadioToScore0to10 = (raw) => {
  if (typeof raw === 'string') {
    const v = raw.trim().toLowerCase();
    if (v === 'yes' || v === 'yes_') return 10;
    if (v === 'no_not_on_a_roadmap' || v === 'no_not_on_a_roadmap_') return 10;
    if (v === 'may_be' || v === 'maybe' || v === 'may be') return 5;
    if (v === 'partially' || v === 'partially_') return 5;
    if (v === 'no' || v === 'no_') return 0;
    if (v === 'yes_on_a_roadmap' || v === 'yes_on_a_roadmap_') return 0;
    return coerceNum(raw);
  }
  if (typeof raw === 'boolean') return raw ? 10 : 0;
  return coerceNum(raw);
};

/**
 * Calculate weighted ratings (no distribution):
 * - weights: [{ id, weight }]
 * - responses: [{ id, value }] (value can be option key array)
 * - totalStars: max rating scale (default 10)
 * - optionKeyValueMap: produced by buildOptionKeyValueMap(formDefinition)
 */
const calculateWeightedRatings = (weights, responses, totalStars = 10, optionKeyValueMap = {}) => {
  const diagnostics = [];
  const errors = [];

  const respArr = Array.isArray(responses) ? responses : [];
  const weightsArr = Array.isArray(weights) ? weights : [];

  const totalStarsNum = coerceNum(totalStars);
  if (!Number.isFinite(totalStarsNum) || totalStarsNum <= 0) {
    errors.push(`Invalid totalStars: "${totalStars}" → must be a positive number.`);
    return {
      totalQuestions: respArr.length,
      weightedQuestions: weightsArr.length,
      totalWeightage: 0,
      individualRatings: [],
      totalRating: 0,
      percentage: 0,
      diagnostics,
      errors
    };
  }

  // Build weight map (first occurrence wins)
  const buildWeightMapResult = weightsArr.reduce(
    (acc, w) => {
      const id = w && w.id;
      const weightNum = coerceNum(w && w.weight);

      if (!id) {
        acc.diagnostics.push('A weight item without id was ignored.');
        return acc;
      }

      if (!Object.prototype.hasOwnProperty.call(acc.map, id)) {
        acc.map[id] = weightNum;
        if (!Number.isFinite(weightNum)) {
          acc.diagnostics.push(`Weight for id "${id}" is non-numeric ("${w && w.weight}").`);
        }
      } else {
        acc.diagnostics.push(`Duplicate weight entry for id "${id}". First occurrence used.`);
      }

      return acc;
    },
    { map: {}, diagnostics: [] }
  );

  diagnostics.push(...buildWeightMapResult.diagnostics);
  const weightMap = buildWeightMapResult.map;

  const totalQuestions = respArr.length;
  const weightedQuestions = Object.values(weightMap).filter(Number.isFinite).length;
  const totalWeightageNumeric = Object.values(weightMap).reduce(
    (sum, w) => sum + (Number.isFinite(w) ? w : 0),
    0
  );

  // Compute ratings
  const computeResult = respArr.reduce(
    (acc, resp) => {
      const id = resp && resp.id;

      const rawAnswer = resolveRawAnswer(resp, optionKeyValueMap);
      const received = mapRadioToScore0to10(rawAnswer);

      const mappedWeight = Object.prototype.hasOwnProperty.call(weightMap, id)
        ? weightMap[id]
        : 0; // no distribution

      const weight = Number.isFinite(mappedWeight) ? mappedWeight : 0;
      if (!Number.isFinite(mappedWeight)) {
        acc.diagnostics.push(`Weight for id "${id}" is NaN; using 0.`);
      }

      let rating = NaN;
      if (!Number.isFinite(received)) {
        acc.diagnostics.push(
          `Received value for id "${id}" is non-numeric ("${Array.isArray(rawAnswer) ? JSON.stringify(rawAnswer) : String(rawAnswer)}").`
        );
      } else {
        rating = (received / totalStarsNum) * (weight / 100);
        if (Number.isFinite(rating)) {
          acc.totalRatingFloat += rating;
        } else {
          acc.diagnostics.push(`Computed rating for id "${id}" is NaN.`);
        }
      }

      acc.rows.push({
        id,
        received: Number.isFinite(received) ? received : null,
        weight,
        rating: Number.isFinite(rating) ? rating.toFixed(4) : 'NaN',
        rawAnswer
      });

      return acc;
    },
    { rows: [], totalRatingFloat: 0, diagnostics: [] }
  );

  diagnostics.push(...computeResult.diagnostics);
  const percentage = Math.round(computeResult.totalRatingFloat * 100);

  return {
    totalQuestions,
    weightedQuestions,
    totalWeightage: Number(totalWeightageNumeric.toFixed(4)),
    individualRatings: computeResult.rows,
    totalRating: Math.round(computeResult.totalRatingFloat),
    percentage,
    diagnostics,
    errors
  };
};



const EvaluationPopupForm = (props) => {

  const { jsonData = [], ideaIdKey, challengeKey, loginUserEmail, handleEvaluatorRatingSave, getJsonEvaluatorForRating, getJsonEvaluatorForRatingIsLoading, setDialogEvaluatorRateOpen, isHackathonChallenge } = props || {}
  // console.log("🚀 ~ EvaluationPopupForm ~ props:", props)
  const [formData, setFormData] = useState([]);
  const [formDefinition, setFormDefinition] = useState([]);
  const [error, setError] = useState('');
  // const mean = calculateMeanPercentage(formData);
  const scorecardPopupIdeaEvaluationId = getJsonEvaluatorForRating?.id
  const ideaEvaluationScoreCardAnswerJson = getJsonEvaluatorForRating?.scoreCardAnswerJson?.value
  const scorecardPopupFlagRecevive = getJsonEvaluatorForRating?.businessScoreCardFlag
  const ratingStatusFlagReceive = getJsonEvaluatorForRating?.ratingStatus
  const ratingStatusIsPending = String(ratingStatusFlagReceive ?? '').trim().toUpperCase() === 'PENDING';
  console.log("ratingStatusFlagReceiveBoolean", ratingStatusIsPending)

  const prefillAnswers = useMemo(() => {
    let raw = ideaEvaluationScoreCardAnswerJson

    // Handle cases where scoreCardAnswerJson can be { value: ... }
    if (raw && typeof raw === 'object' && 'value' in raw) {
      raw = raw.value;
    }

    return parseArraySafely(raw, []);
  }, [ideaEvaluationScoreCardAnswerJson]);



  // let convertideaEvaluationScoreCardAnswerJson;
  // try {
  //  convertideaEvaluationScoreCardAnswerJson = JSON.parse(ideaEvaluationScoreCardAnswerJson);
  // } catch (e) {
  //   // If parsing fails (e.g., blank string), treat as invalid/empty
  //   convertideaEvaluationScoreCardAnswerJson = [];
  // }


  // const convertJsonParseData = fixAndParseJSON(jsonData)
  // const convertJsonParseData = JSON.parse(jsonData) working...


  let convertJsonParseData = null;

  try {
    if (jsonData && typeof jsonData === "string") {
      convertJsonParseData = JSON.parse(jsonData);
    } else {
      console.warn("jsonData is empty or not a string:", jsonData);
    }
  } catch (err) {
    console.error("Failed to parse JSON:", err);
    convertJsonParseData = {}; // fallback to empty object or handle gracefully
  }


  const questions = useMemo(() => parseArraySafely(convertJsonParseData, []), [convertJsonParseData]);
  const prefill = useMemo(() => parseArraySafely(prefillAnswers, []), [prefillAnswers]);

  // find only the weight key along with its id from your array of objects,
  // 1st find out weight with id and set with array obj
  // const weightWithIdArr = formDefinition
  // .filter(item => item.weight !== undefined) // only objects that have weight
  // .map(item => ({ id: item.id, weight: item.weight }));

  // 2) Build weights from the form definition (elements with numeric weight)
  const weightWithIdArr = (Array.isArray(questions) ? questions : [])
    .filter(el => Number.isFinite(coerceNum(el && el.weight)))
    .map(el => ({ id: el.id, weight: coerceNum(el.weight) }));
  // console.log("weightWithId==>",weightWithIdArr);
  // console.log("formData before render==>",formData);


  // To extract only the id and value for elements where the name starts with "rating_", 
  // const ratingValuesArr = formData
  //   .filter(item => item.name.startsWith('rating_')) // only rating elements
  //   .map(item => ({ id: item.id, value: item.value }));

  const handleFormChange = useCallback(
    async (data) => {
      setFormData(data);
      // console.log("rating===> calcalution==> :", data)
      const optionKeyValueMapRadio = buildOptionKeyValueMap(questions);
      const calcWeightedRating = calculateWeightedRatings(weightWithIdArr, data, 10, optionKeyValueMapRadio);
      const roundCalcRating = calcWeightedRating?.percentage


      const reqWithParams = isHackathonChallenge ? {
        ideaIdKey,
        challengeKey,
        scoreCardJson: convertJsonParseData || [],
        scoreCardAnswerJson: data || [],
        scoreCardRating: roundCalcRating || 0,
        userEmail: loginUserEmail,
        ideaEvaluationId: scorecardPopupIdeaEvaluationId,
        businessSCFlag: scorecardPopupFlagRecevive || 'E',
      } : {
        ideaIdKey,
        challengeKey,
        scoreCardJson: convertJsonParseData || [],
        scoreCardAnswerJson: data || [],
        scoreCardRating: roundCalcRating || 0,
        userEmail: loginUserEmail,
        ideaEvaluationId: scorecardPopupIdeaEvaluationId,
        businessSCFlag: scorecardPopupFlagRecevive,
      };

      try {
        await handleEvaluatorRatingSave(reqWithParams);
        if (isHackathonChallenge ?? false) {
          setDialogEvaluatorRateOpen(false);
        }
      } catch (err) {
        console.log('Saved form data Err:', err);
      }

    }, [scorecardPopupIdeaEvaluationId, scorecardPopupFlagRecevive, loginUserEmail]);


  if (error) {
    return <div style={{ color: 'red' }}>Error: {error}</div>;
  }


  if (getJsonEvaluatorForRatingIsLoading) {
    return 'Loading...'
  }

  return (
    <div>
      <ReactFormGenerator
        data={questions}
        answer_data={prefill || []}
        // onBlur={handleFormChangeTest}
        onSubmit={handleFormChange}
        hide_actions={!ratingStatusIsPending}
        submitButton={ 
          <CustomButton
            type="submit"
            variant="contained"
            color="primary"
            borderRadius='reactangle'
          >
            {getJsonEvaluatorForRatingIsLoading ? "loading... " : "Submit"}
          </CustomButton>
        }
      />
    </div>
  );
};

export default EvaluationPopupForm;
